import axios from 'axios';
import api, { NEXTJS_API_URL, API_DEBUG } from './client';

export const biodataAPI = {
  getAllBiodata: async (params = {}) => {
    try {
      const response = await axios.get(`${NEXTJS_API_URL}/biodatas`, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      const payload = response.data || {};
      const records = payload.data?.data || payload.data || [];
      const pagination = payload.data?.pagination || payload.pagination || {};

      return {
        status: payload.status || "success",
        data: {
          data: records,
          pagination,
        },
        records,
        pagination,
        message: payload.message,
      };
    } catch (error) {
      console.error("Error fetching biodata list:", error);
      return {
        status: "error",
        message: error.response?.data?.error || error.message,
        data: { data: [], pagination: {} },
        records: [],
        pagination: {},
      };
    }
  },

  getBiodataById: async (id) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      };

      // Single call: Next.js biodata detail includes documents/photos
      const response = await axios.get(`${NEXTJS_API_URL}/biodatas/${id}`, {
        headers,
      });
      if (API_DEBUG) {
      }

      const payload = response.data || {};
      const data = payload?.data?.data ?? payload?.data ?? payload ?? null;
      if (API_DEBUG) {
      }

      if (!data) {
        console.error("❌ No biodata data found in response");
        return {
          status: "error",
          message: "No biodata data in response",
          data: null,
        };
      }

      return {
        status: "success",
        data,
      };
    } catch (error) {
      console.error("❌ Error fetching biodata:", error);
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
      return {
        status: "error",
        message: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  createBiodata: async (biodataData) => {
    try {
      const response = await axios.post(
        `${NEXTJS_API_URL}/biodatas`,
        biodataData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      const payload = response.data || {};
      const data = payload?.data ?? payload ?? null; // Next.js returns plain biodata object
      return {
        success: true,
        status: "success",
        data,
        message: payload.message || "Saved successfully",
      };
    } catch (error) {
      console.error("Error creating biodata:", error);
      return {
        success: false,
        status: "error",
        message: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  updateBiodata: async (id, biodataData) => {
    try {
      const response = await axios.put(
        `${NEXTJS_API_URL}/biodatas/${id}`,
        biodataData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      const payload = response.data || {};
      const data = payload?.data ?? payload ?? null; // Next.js returns plain biodata object
      return {
        success: true,
        status: "success",
        data,
        message: payload.message || "Saved successfully",
      };
    } catch (error) {
      console.error("Error updating biodata:", error);
      return {
        success: false,
        status: "error",
        message: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Get complete biodata with all related data (next_of_kin, documents, photos, medicals)
  getCompleteBiodataById: async (id) => {
    const response = await api.get(`/biodatas/${id}/complete`);
    return response.data;
  },

  deleteBiodata: async (id) => {
    try {
      const response = await api.delete(`/biodatas/${id}`);
      const payload = response.data || {};
      return {
        status: "success",
        message: payload.message || "Biodata deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting biodata:", error);
      return {
        status: "error",
        message: error.response?.data?.error || error.message,
      };
    }
  },

  submitBiodata: async (id) => {
    const response = await api.post(`/biodatas/${id}/submit`);
    return response.data;
  },

  approveBiodata: async (id) => {
    const response = await api.post(`/biodatas/${id}/approve`);
    return response.data;
  },

  rejectBiodataBulk: async (rejectData) => {
    const response = await api.post("/biodata-rejections/bulk", rejectData);
    return response.data;
  },

  rejectBiodata: async (id, rejectData) => {
    const response = await api.post(`/biodatas/${id}/reject`, rejectData);
    return response.data;
  },

  getBiodataByStatus: async (status, params = {}) => {
    try {
      const isAmended = String(status).toLowerCase() === "resubmitted";
      const url = isAmended
        ? `${NEXTJS_API_URL}/clab/amended-biodatas`
        : `${NEXTJS_API_URL}/biodatas/status/${status}`;
      const response = await axios.get(url, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      const payload = response.data || {};
      const records = payload.data?.data || [];
      const pagination = payload.data?.pagination || {};

      const isSuccess = payload.status === "success";

      if (isSuccess) {
        return {
          success: true,
          status: "success",
          data: {
            data: records,
            pagination,
          },
          records,
          pagination,
          message: payload.message,
        };
      }

      return {
        success: false,
        status: "error",
        data: { data: [], pagination: {} },
        records: [],
        pagination: {},
        message: payload.message || "Failed to load biodata",
        error: payload.message || "Failed to load biodata",
      };
    } catch (error) {
      console.error("Error fetching biodata by status:", error);
      return {
        success: false,
        status: "error",
        data: { data: [], pagination: {} },
        records: [],
        pagination: {},
        message: error.response?.data?.error || error.message,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  getStatusCounts: async () => {
    try {
      // Use Next.js API for status counts
      const response = await axios.get(
        `${NEXTJS_API_URL}/biodatas/status-counts`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      // Next.js API returns the counts directly (not wrapped in status object)
      // Map the response to match what React expects
      const counts = response.data;
      return {
        status: "success",
        data: {
          drafts: counts.draft || 0,
          submitted: counts.pending || 0,
          approved: counts.approved || 0,
          rejected: counts.rejected || 0,
          selected: counts.selected || 0,
          resubmitted: counts.resubmitted || 0,
        },
      };
    } catch (error) {
      console.error("Error fetching status counts:", error);
      return {
        status: "error",
        message: error.message,
        data: {
          drafts: 0,
          submitted: 0,
          approved: 0,
          rejected: 0,
          selected: 0,
          resubmitted: 0,
        },
      };
    }
  },

  getPendingApproval: async () => {
    const response = await api.get("/clab/pending-biodatas");
    return response.data;
  },

  getAvailableForEmployers: async () => {
    const response = await api.get("/available-biodatas");
    return response.data;
  },

  checkPassportExists: async (passportNo) => {
    const response = await api.get(`/biodatas/passport/${passportNo}/exists`);
    return response.data;
  },

  // File operations
  downloadBiodata: async (id) => {
    const response = await api.get(`/biodatas/${id}/download`, {
      responseType: "blob",
    });
    return response.data;
  },

  bulkUploadBiodata: async (file) => {
    try {
      // Use Next.js API for bulk uploads (to be implemented)
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${NEXTJS_API_URL}/biodatas/bulk-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      return {
        status: "success",
        data: response.data,
      };
    } catch (error) {
      console.error("Error bulk uploading biodata:", error);
      return {
        status: "error",
        message: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  uploadDocument: async (biodataId, file, type) => {
    try {
      // Use Next.js API for document uploads
      const formData = new FormData();
      formData.append("document", file);
      formData.append("document_type", type);

      const response = await axios.post(
        `${NEXTJS_API_URL}/biodatas/${biodataId}/documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      return {
        status: "success",
        data: response.data,
      };
    } catch (error) {
      console.error("Error uploading document:", error);
      return {
        status: "error",
        message: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  uploadPhoto: async (biodataId, file, type) => {
    try {
      // Use Next.js API for photo uploads
      const formData = new FormData();
      formData.append("photo", file);
      formData.append("photo_type", type);

      const response = await axios.post(
        `${NEXTJS_API_URL}/biodatas/${biodataId}/photos`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      return {
        status: "success",
        data: response.data,
      };
    } catch (error) {
      console.error("Error uploading photo:", error);
      return {
        status: "error",
        message: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  getPhotos: async (biodataId) => {
    try {
      const response = await axios.get(
        `${NEXTJS_API_URL}/biodatas/${biodataId}/photos`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      const payload = response.data;
      const photos = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.photos)
        ? payload.photos
        : [];
      return {
        status: "success",
        data: photos,
      };
    } catch (error) {
      console.error("Error fetching photos:", error);
      return {
        status: "error",
        message: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  getDocuments: async (biodataId) => {
    try {
      const response = await axios.get(
        `${NEXTJS_API_URL}/biodatas/${biodataId}/documents`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      const payload = response.data;
      const documents = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.documents)
        ? payload.documents
        : [];
      return {
        status: "success",
        data: documents,
      };
    } catch (error) {
      console.error("Error fetching documents:", error);
      return {
        status: "error",
        message: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Generic file upload for biodata (before biodata is created)
  uploadFile: async (formData) => {
    try {
      // Use Next.js API for file uploads
      const response = await axios.post(
        `${NEXTJS_API_URL}/biodatas/upload-file`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      return {
        status: "success",
        data: response.data,
      };
    } catch (error) {
      console.error("Error uploading file:", error);
      return {
        status: "error",
        message: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Edit permission management
  unlockBiodata: async (id, unlockData) => {
    const response = await api.post(`/biodatas/${id}/unlock`, unlockData);
    return response.data;
  },

  getEditPermission: async (id) => {
    const response = await api.get(`/biodatas/${id}/permission`);
    return response.data;
  },

  resubmitBiodata: async (id) => {
    const response = await api.post(`/biodatas/${id}/resubmit`);
    return response.data;
  },

  resubmitAmendedBiodata: async (id) => {
    const response = await api.post(`/biodatas/${id}/resubmit-amended`);
    return response.data;
  },

  resubmitAmended: async (id) => {
    const response = await api.post(`/biodatas/${id}/resubmit-amended`);
    return response.data;
  },

  lockBiodata: async (id) => {
    const response = await api.patch(`/biodatas/${id}/lock`);
    return response.data;
  },

  getBiodataLogs: async (id) => {
    const response = await api.get(`/biodatas/${id}/logs`);
    return response.data;
  },

  // Removed: updateStatus (no Next.js endpoint). Use CLAB approve/reject routes instead.

  grantEditPermission: async (id, data) => {
    const response = await api.post(
      `/biodatas/${id}/grant-edit-permission`,
      data
    );
    return response.data;
  },

  submit: async (id) => {
    const response = await api.post(`/biodatas/${id}/submit`);
    return response.data;
  },

  download: async (id) => {
    const response = await api.get(`/biodatas/${id}/download`, {
      responseType: "blob",
    });
    return response.data;
  },

  getDocuments: async (biodataId) => {
    const response = await api.get(`/biodatas/${biodataId}/documents`);
    const payload = response.data;
    const documents = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.documents)
      ? payload.documents
      : [];
    return documents;
  },

  addDocument: async (biodataId, file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    const response = await api.post(
      `/biodatas/${biodataId}/documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  deleteDocument: async (id) => {
    const response = await api.delete(`/documents/${id}`);
    return response.data;
  },

  downloadDocument: async (id) => {
    const response = await api.get(`/documents/${id}/download`, {
      responseType: "blob",
    });
    return response.data;
  },

  getPhotos: async (biodataId) => {
    const response = await api.get(`/biodatas/${biodataId}/photos`);
    const payload = response.data;
    const photos = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.photos)
      ? payload.photos
      : [];
    return photos;
  },

  addPhoto: async (biodataId, file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    const response = await api.post(`/biodatas/${biodataId}/photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  deletePhoto: async (id) => {
    const response = await api.delete(`/photos/${id}`);
    return response.data;
  },

  setPrimaryPhoto: async (id) => {
    const response = await api.patch(`/photos/${id}/set-primary`);
    return response.data;
  },

  getPhoto: async (id) => {
    const response = await api.get(`/photos/${id}`);
    return response.data;
  },

  getMedicals: async (biodataId) => {
    const response = await api.get(`/biodatas/${biodataId}/medicals`);
    return response.data;
  },

  addMedical: async (biodataId, data) => {
    const response = await api.post(`/biodatas/${biodataId}/medicals`, data);
    return response.data;
  },

  getMedical: async (id) => {
    const response = await api.get(`/medicals/${id}`);
    return response.data;
  },

  updateMedical: async (id, data) => {
    const response = await api.put(`/medicals/${id}`, data);
    return response.data;
  },

  downloadMedical: async (id) => {
    const response = await api.get(`/medicals/${id}/download`, {
      responseType: "blob",
    });
    return response.data;
  },

  deleteMedical: async (id) => {
    const response = await api.delete(`/medicals/${id}`);
    return response.data;
  },

  getStatusLogs: async (biodataId) => {
    const response = await api.get(`/biodatas/${biodataId}/status-logs`);
    return response.data;
  },

  addStatusLog: async (biodataId, data) => {
    const response = await api.post(`/biodatas/${biodataId}/status-logs`, data);
    return response.data;
  },

  getStatusLog: async (id) => {
    const response = await api.get(`/status-logs/${id}`);
    return response.data;
  },

  getStatusSummary: async (biodataId) => {
    const response = await api.get(`/biodatas/${biodataId}/status-summary`);
    return response.data;
  },

  getRejections: async (biodataId) => {
    const response = await api.get(`/biodatas/${biodataId}/rejections`);
    return response.data;
  },

  addRejection: async (biodataId, data) => {
    const response = await api.post(`/biodatas/${biodataId}/rejections`, data);
    return response.data;
  },

  getLatestRejection: async (biodataId) => {
    const response = await api.get(`/biodatas/${biodataId}/latest-rejection`);
    return response.data;
  },

  getRejection: async (id) => {
    const response = await api.get(`/biodata-rejections/${id}`);
    return response.data;
  },

  updateRejection: async (id, data) => {
    const response = await api.put(`/biodata-rejections/${id}`, data);
    return response.data;
  },

  deleteRejection: async (id) => {
    const response = await api.delete(`/biodata-rejections/${id}`);
    return response.data;
  },

  bulkReject: async (data) => {
    const response = await api.post("/biodata-rejections/bulk", data);
    return response.data;
  },
};

// Application Management API
